import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useUserContext } from '../../context/users/UserContext'
import { getQuestionById, updateQuestion } from "../../context/questions/QuestionActions";
import { 
  FaQuestionCircle, 
  FaFileAlt, 
  FaSave,
  FaExclamationTriangle,
  FaPen
} from 'react-icons/fa'
import { Card, Row, Col, Form, Button } from 'react-bootstrap'
import Select from 'react-select'
import { getDescriptionOptions } from '../../utils/questionDescriptions'
import './UpdateQuestions.css'

// eslint-disable-next-line
export default function UpdateQuestions(props) {
  const navigate = useNavigate()
  const { authenticatedUser, setAlert } = useUserContext()
  const params = useParams()

  const [question, setQuestion] = useState('')
  const [description, setDescription] = useState(null)
  const [type, setType] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    const getData = async () => {
      try {
        const response = await getQuestionById({ 
          path: 'questions',
          id: params.id,
          token: authenticatedUser.value 
        })

        if (response && typeof response === 'object') {
          setQuestion(response.question || '')
          // Buscar la descripción en las opciones disponibles
          const descriptionOption = getDescriptionOptions().find(opt => opt.value === response.description)
          setDescription(descriptionOption || null)
          setType(response.type || '')
        } else {
          setAlert({
            message: 'Error al cargar los datos de la pregunta',
            msgType: 'warning'
          })
          navigate('/preguntas')
        }
      } catch (error) {
        setAlert({
          message: 'Error al cargar los datos de la pregunta',
          msgType: 'warning'
        })
        navigate('/preguntas')
      }
    }
    getData()
  }, [params.id, authenticatedUser.value, navigate, setAlert])

  const handleChangeQuestion = (e) => setQuestion(e.target.value)
  const handleChangeDescription = (selectedOption) => setDescription(selectedOption)
  const handleChangeType = (e) => setType(e.target.value)

  const validateForm = () => {
    const newErrors = {}

    if (!question.trim()) {
      newErrors.question = 'La pregunta es requerida'
    }

    if (!description || !description.value) {
      newErrors.description = 'La descripción es requerida'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      let response = await updateQuestion({ 
        path: 'questions',
        id: params.id,
        data: {
          question: question.trim(),
          description: description.value,
          type: type.trim() || null
        },
        token: authenticatedUser.value 
      })

      if (response.status === 200) {
        setAlert({
          message: 'Pregunta actualizada exitosamente',
          msgType: 'success'
        })
        navigate('/preguntas')
      } else {
        setAlert({
          message: response.message || 'Error al actualizar la pregunta',
          msgType: 'warning'
        })
      }
    } catch (error) {
      setAlert({
        message: 'Error al actualizar la pregunta',
        msgType: 'warning'
      })
    } finally {
      setIsLoading(false)
    }
  }
  
  window.scrollTo({
    top: 0,
    behavior: 'auto',
  })

  return (
    <div className="update-question-container">
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <div className="header-content">
            <div className="header-icon">
              <FaPen />
            </div>
            <div className="header-text">
              <h1>Editar Pregunta</h1>
              <p>Modifica la información de la pregunta del sistema</p>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <Card className="form-card">
          <Card.Header>
            <h3>
              <FaQuestionCircle className="field-icon" />
              Información de la Pregunta
            </h3>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row>
                {/* Question Field */}
                <Col lg={12} md={12} sm={12} className="mb-4">
                  <Form.Group>
                    <Form.Label className="form-label">
                      <FaQuestionCircle className="field-icon" />
                      Pregunta
                      <span className="required-field">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Escribe la pregunta aquí..."
                      value={question}
                      onChange={handleChangeQuestion}
                      className={`form-control-custom ${errors.question ? 'is-invalid' : ''}`}
                    />
                    {errors.question && (
                      <div className="error-message">
                        <FaExclamationTriangle className="error-icon" />
                        {errors.question}
                      </div>
                    )}
                  </Form.Group>
                </Col>

                {/* Description Field */}
                <Col lg={12} md={12} sm={12} className="mb-4">
                  <Form.Group>
                    <Form.Label className="form-label">
                      <FaFileAlt className="field-icon" />
                      Descripción
                      <span className="required-field">*</span>
                    </Form.Label>
                    <Select
                      value={description}
                      onChange={handleChangeDescription}
                      options={getDescriptionOptions()}
                      placeholder="Selecciona una descripción..."
                      isSearchable={true}
                      classNamePrefix="react-select"
                      className={errors.description ? 'is-invalid' : ''}
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          border: errors.description ? '2px solid #dc3545' : '2px solid #e9ecef',
                          borderRadius: '8px',
                          padding: '0.5rem',
                          fontSize: '0.95rem',
                          transition: 'all 0.3s ease',
                          background: state.isFocused ? 'white' : '#f8f9fa',
                          boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(254, 133, 14, 0.25)' : 'none',
                          '&:hover': {
                            borderColor: errors.description ? '#dc3545' : '#FE850E'
                          }
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: '#adb5bd',
                          fontStyle: 'italic'
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected ? '#FE850E' : state.isFocused ? '#f8f9fa' : 'white',
                          color: state.isSelected ? 'white' : '#2c3e50',
                          '&:hover': {
                            backgroundColor: state.isSelected ? '#FE850E' : '#e9ecef'
                          }
                        })
                      }}
                    />
                    {errors.description && (
                      <div className="error-message">
                        <FaExclamationTriangle className="error-icon" />
                        {errors.description}
                      </div>
                    )}
                  </Form.Group>
                </Col>

                {/* Type Field (Optional) */}
                <Col lg={12} md={12} sm={12} className="mb-4">
                  <Form.Group>
                    <Form.Label className="form-label">
                      <FaFileAlt className="field-icon" />
                      Tipo (Opcional)
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Tipo de pregunta (ej: múltiple opción, texto libre)"
                      value={type}
                      onChange={handleChangeType}
                      className="form-control-custom"
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Form Actions */}
              <div className="form-actions">
                <Button 
                  type="button" 
                  variant="outline-secondary"
                  className="cancel-button"
                  onClick={() => navigate('/preguntas')}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="save-button"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Actualizando Pregunta...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      Actualizar Pregunta
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </div>
  )
}
